import type { Experiment, ExperimentResultRow } from '../api.js';
import { formatNumber } from '../lib/format.js';

/**
 * §65.1 — deney SONUÇ yüzeyi: durum etiketi, durum cümlesi ve kol tablosu.
 *
 * Liste kartı (`Deneyler.tsx`) ile detay sayfası (`DeneyDetay.tsx`) AYNI parçaları
 * çizer; §102.2 gereği detay listeyi ikame etmez, derinleştirir. İki yerde iki
 * kopya olsaydı, DEĞİŞMEZ KURALIN (aşağıda) yalnız birinde tutulması an meselesiydi
 * — Faz 7'nin kopya-ayrışma dersi.
 *
 * DEĞİŞMEZ KURAL (README + §65.1): motor `concluded` demeden "kazanan/önde"
 * KELİMESİ GEÇMEZ. Sürerken yalnız kol sayıları + always-valid aralık gösterilir.
 */

export const STATUS_META: Record<
  Experiment['status'],
  { label: string; tone: 'neutral' | 'success' | 'warning' }
> = {
  draft: { label: 'Taslak', tone: 'neutral' },
  running: { label: 'Sürüyor', tone: 'success' },
  stopped: { label: 'Durduruldu', tone: 'warning' },
  concluded: { label: 'Sonuçlandı', tone: 'neutral' },
};

function pct1(v: number): string {
  return `${(v * 100).toFixed(1).replace('.', ',')}`;
}

/** §65.1 — sonuç satırı: sayılar experiment_results'tan AYNEN (panel hesap yapmaz). */
export function ResultsTable({
  experiment,
  results,
}: {
  experiment: Experiment;
  results: ExperimentResultRow[];
}) {
  if (results.length === 0) {
    return <p className="text-[13px] text-[#9ca3af]">Veri toplanıyor — henüz değerlendirme yok.</p>;
  }
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-left text-[12px] text-[#9ca3af]">
          <th className="py-1 pr-4">Varyant</th>
          <th className="py-1 pr-4">Oturum</th>
          <th className="py-1 pr-4">Dönüşüm</th>
          <th className="py-1 pr-4">CR</th>
          <th className="py-1 pr-4">Lift (always-valid aralık)</th>
          <th className="py-1">p</th>
        </tr>
      </thead>
      <tbody>
        {results.map((r) => {
          const cr = r.sessions > 0 ? r.conversions / r.sessions : 0;
          const kazanan = experiment.status === 'concluded' && experiment.winner === r.variant;
          return (
            <tr key={r.variant} className="border-t border-[#f3f4f6]">
              <td className="py-1.5 pr-4 font-medium">
                {r.variant}
                {kazanan ? (
                  <span className="ml-2 rounded bg-brand/10 px-1.5 py-0.5 text-[11px] font-semibold text-brand">
                    kazanan
                  </span>
                ) : null}
              </td>
              <td className="num py-1.5 pr-4">{formatNumber(r.sessions)}</td>
              <td className="num py-1.5 pr-4">{formatNumber(r.conversions)}</td>
              <td className="num py-1.5 pr-4">%{pct1(cr)}</td>
              <td className="num py-1.5 pr-4">
                {r.liftMid === null
                  ? '—'
                  : `%${pct1(r.liftMid)} (%${pct1(r.liftLow ?? 0)} … %${pct1(r.liftHigh ?? 0)})`}
              </td>
              <td className="num py-1.5">{r.pValue === null ? '—' : r.pValue.toFixed(4).replace('.', ',')}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function DurumMetni({
  experiment,
  results,
}: {
  experiment: Experiment;
  results: ExperimentResultRow[];
}) {
  if (experiment.status === 'concluded') {
    return (
      <p className="text-[13px] font-medium text-[#111827]">
        Sonuçlandı:{' '}
        {experiment.winner === null
          ? 'sonuçsuz — kollar arasında güvenilir fark bulunamadı'
          : `${experiment.winner} kazandı`}
      </p>
    );
  }
  const degerlendirilmis = results.some((r) => r.pValue !== null);
  return (
    <p className="text-[13px] text-[#6b7280]">
      {degerlendirilmis ? 'Sürüyor — henüz sonuç yok (aralık daralıyor).' : 'Veri toplanıyor.'}
    </p>
  );
}
